import React, { createContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe } from '../api/authApi';
import { resolveRole } from '../utils/roleUtils';

// Context oluşturma
export const AuthContext = createContext();

// Provider bileşeni
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sayfa yüklendiğinde token ile kullanıcı bilgisini getir
  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const userData = await getMe(token);
          
          // Kullanıcı verisini kontrol et
          if (!userData.role_id) {
            console.warn('Kullanıcı verisinde role_id yok, yetki kontrolleri çalışmayabilir');
          }
          
          // Geriye uyumluluk - role_name'i role olarak da tut
          if (userData.role_name && !userData.role) {
            userData.role = userData.role_name;
          }
          
          setUser(userData);
        } catch (err) {
          console.error('Kullanıcı bilgileri alınamadı:', err);
          logout(); // Token geçersizse çıkış yap
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  // Giriş işlemi
  const login = async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiLogin(username, password);
      
      // Geriye uyumluluk - role_name'i role olarak da tut
      if (data.role_name && !data.role) {
        data.role = data.role_name;
      }
      
      // Kullanıcının 2FA durumunu kontrol et
      if (data.is_2fa_enabled) {
        // 2FA etkinse, token'ı saklamadan kullanıcı verilerini geri döndür
        return data;
      }

      // 2FA etkin değilse, normal giriş işlemine devam et
      setUser(data);
      setToken(data.token);
      localStorage.setItem('token', data.token);
      return data;
    } catch (err) {
      console.error('Giriş hatası:', err);
      setError(err.message || 'Giriş başarısız.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Çıkış işlemi
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Rol kontrolü - role_id ile
  const hasRole = (requiredRole) => {
    if (!user || !user.role_id) return false;

    // role_id kullanarak kontrol et
    const roleObj = resolveRole(user.role_id);
    if (roleObj) {
      // Admin ve CASI rolleri her şeyi görebilir
      if (roleObj.name === 'admin' || roleObj.name === 'casi') {
        return true;
      }
      
      // İsim veya ID ile kontrol
      if (typeof requiredRole === 'number') {
        return roleObj.id === requiredRole;
      }
      return roleObj.name === requiredRole;
    }

    // Role objesi çözülemezse, ID ile basit karşılaştırma
    if (typeof requiredRole === 'number') {
      return user.role_id === requiredRole;
    }
    
    // String role adı için sadece admin ve casi kontrolü
    return requiredRole === 'admin' && user.role_id === 1 || 
           requiredRole === 'casi' && user.role_id === 2;
  };

  const authContextValue = {
    isAuthenticated: !!user,
    user,
    token,
    loading,
    error,
    login,
    logout,
    hasRole
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};